import React from 'react';
import { ExternalLink, Sparkles, Flame, ArrowRight } from 'lucide-react';

const SMARTLINK_URL = 'https://www.profitableratecpmnetwork.com/fwns2m27fd?key=29a35503d166182e6f8b35962fef21e8';

interface AdsterraSmartlinkPromoProps {
  variant?: 'banner' | 'card' | 'badge' | 'bar';
  className?: string;
  title?: string;
  subtitle?: string;
  ctaText?: string;
}

export const AdsterraSmartlinkPromo: React.FC<AdsterraSmartlinkPromoProps> = ({
  variant = 'banner',
  className = '',
  title,
  subtitle,
  ctaText,
}) => {
  if (variant === 'badge') {
    return (
      <a
        href={SMARTLINK_URL}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-rose-500 to-amber-500 px-3 py-1 text-xs font-semibold text-white shadow-xs hover:opacity-95 transition-opacity ${className}`}
      >
        <Sparkles className="h-3 w-3" />
        <span>{title || 'Hot Deals'}</span>
        <ExternalLink className="h-3 w-3 ml-0.5" />
      </a>
    );
  }

  if (variant === 'card') {
    return (
      <div className={`overflow-hidden rounded-2xl border border-rose-200/80 bg-gradient-to-br from-rose-50/70 via-white to-amber-50/50 p-4 sm:p-5 shadow-xs transition hover:shadow-md ${className}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-white shadow-xs">
              <Flame className="h-3.5 w-3.5" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600 bg-rose-100/80 px-2 py-0.5 rounded-full">
              Featured Deal
            </span>
          </div>
          <span className="text-[10px] text-muted-foreground uppercase font-semibold">Sponsored</span>
        </div>
        <h4 className="font-heading text-base font-bold text-foreground mb-1">
          {title || 'Special Discounts & Offers'}
        </h4>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          {subtitle || 'Discover verified promotions, exclusive bargains, and trending deals from partner brands.'}
        </p>
        <a
          href={SMARTLINK_URL}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-rose-600 to-rose-500 px-4 py-2.5 text-xs font-semibold text-white shadow-xs transition hover:from-rose-700 hover:to-rose-600 active:scale-[0.99]"
        >
          <span>{ctaText || 'Check Partner Deals'}</span>
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    );
  }

  if (variant === 'bar') {
    return (
      <aside aria-label="Partner Offer" className={`w-full bg-gradient-to-r from-slate-900 via-rose-950 to-slate-900 text-white px-4 py-2 ${className}`}>
        <div className="mx-auto max-w-7xl flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="rounded bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide">
              Partner Offer
            </span>
            <span className="font-medium text-slate-200">
              {title || 'Exclusive marketplace discounts & seasonal deals are live.'}
            </span>
          </div>
          <a
            href={SMARTLINK_URL}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="inline-flex items-center gap-1 font-semibold text-rose-300 hover:text-white transition"
          >
            <span>{ctaText || 'View Deals'}</span>
            <ArrowRight className="h-3 w-3" />
          </a>
        </div>
      </aside>
    );
  }

  // Default 'banner' variant
  return (
    <section aria-label="Sponsored Partner Deals" className={`mx-auto max-w-7xl px-4 py-3 ${className}`}>
      <div className="relative overflow-hidden rounded-2xl border border-rose-200/90 bg-gradient-to-r from-rose-600 via-rose-500 to-amber-500 p-4 sm:p-5 text-white shadow-md">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur-xs shadow-inner">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-white/25 text-white px-2 py-0.5 rounded-full">
                  Sponsored Partner
                </span>
                <span className="text-xs font-medium text-rose-100">Verified Offers</span>
              </div>
              <h3 className="font-heading text-base sm:text-lg font-bold leading-snug">
                {title || 'Trending Deals & Exclusive Discounts Across India'}
              </h3>
              <p className="text-xs sm:text-sm text-rose-100 max-w-2xl mt-0.5">
                {subtitle || 'Explore limited-time savings on electronics, lifestyle, home services, and shopping from trusted sponsors.'}
              </p>
            </div>
          </div>
          <a
            href={SMARTLINK_URL}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-xs sm:text-sm font-bold text-rose-600 shadow-md transition hover:bg-rose-50 active:scale-[0.98]"
          >
            <span>{ctaText || 'Explore Deals'}</span>
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default AdsterraSmartlinkPromo;
